import React, { FC, Suspense, useState } from 'react';
import ContentLoader from 'react-content-loader';
import { graphql, useLazyLoadQuery, useMutation } from 'react-relay';
import { Drawer } from 'src/components/widgets/drawer';
import { T } from 'src/shared/helpers';
import theme from 'src/shared/theme';
import type { addAIStagingRulePanel_AssignProductMutation } from './__generated__/addAIStagingRulePanel_AssignProductMutation.graphql';
import type { addAIStagingRulePanel_AIStagingRulesListQuery } from './__generated__/addAIStagingRulePanel_AIStagingRulesListQuery.graphql';
import {
    EmptyState,
    FooterButton,
    ItemActionSuccess,
    ItemDot,
    ItemName,
    ItemNameAction,
    PanelItem,
    PanelItemSpaced,
    StackedBody,
    StackedFooterButtons
} from '../common/styled';

const AddAIStagingRulePanelContent: FC<{
    productId: number;
    productRelayId: string;
    existingRuleIds: string[];
    onBack: () => void;
    onAssigned: () => void;
    onCreateNew: () => void;
}> = ({ productId, productRelayId, existingRuleIds, onBack, onAssigned, onCreateNew }) => {
    const [assigning, setAssigning] = useState<string | null>(null);

    const productNameData = useLazyLoadQuery<any>(
        graphql`
            query addAIStagingRulePanel_ProductNameQuery($id: ID!) {
                node(id: $id) {
                    ... on Model {
                        name
                    }
                }
            }
        `,
        { id: productRelayId }
    );
    const productName = (productNameData?.node as any)?.name || '';

    const data = useLazyLoadQuery<addAIStagingRulePanel_AIStagingRulesListQuery>(
        graphql`
            query addAIStagingRulePanel_AIStagingRulesListQuery {
                aiProductStagingRules {
                    id
                    ruleID
                    name
                }
            }
        `,
        {}
    );

    const allRules = React.useMemo(() => {
        const rules = data?.aiProductStagingRules || [];
        return rules.map((rule) => ({ id: rule!.id, name: rule!.name || rule!.id }));
    }, [data]);

    const [commitAssign] = useMutation<addAIStagingRulePanel_AssignProductMutation>(graphql`
        mutation addAIStagingRulePanel_AssignProductMutation($ruleId: ID!, $productIds: [Long!]!) {
            assignProductsToAIStagingRule(ruleId: $ruleId, productIds: $productIds) {
                result
            }
        }
    `);

    const handleAssign = (rule: { id: string; name: string }) => {
        setAssigning(rule.id);
        commitAssign({
            variables: {
                ruleId: rule.id,
                productIds: [productId]
            },
            onCompleted: () => {
                setAssigning(null);
                onAssigned();
            },
            onError: () => {
                setAssigning(null);
            }
        });
    };

    const availableRules = allRules.filter((rule) => !existingRuleIds.includes(rule.id));

    return (
        <Drawer
            isOpen={true}
            isStacked
            onClose={onBack}
            header={{ title: T._('Add AI lifestyle scene rule', 'Admin'), subtitle: productName }}
            width={580}
            footer={
                <StackedFooterButtons>
                    <FooterButton onClick={onBack}>{T._('Cancel', 'Admin')}</FooterButton>
                </StackedFooterButtons>
            }
        >
            <StackedBody>
                {availableRules.length === 0 && <EmptyState>{T._('All AI lifestyle scene rules are already assigned', 'Admin')}</EmptyState>}
                {availableRules.map((rule) => (
                    <PanelItem key={rule.id} $clickable={assigning === null} onClick={() => assigning === null && handleAssign(rule)}>
                        <ItemDot $color='#3b82f6' />
                        <ItemName>{rule.name}</ItemName>
                        <ItemActionSuccess>{assigning === rule.id ? T._('Assigning...', 'Admin') : T._('Assign', 'Admin')}</ItemActionSuccess>
                    </PanelItem>
                ))}
                <PanelItemSpaced
                    $clickable
                    onClick={() => {
                        onBack();
                        onCreateNew();
                    }}
                >
                    <ItemDot $color='#d1d5db' />
                    <ItemNameAction>+ {T._('Create new', 'Admin')}</ItemNameAction>
                </PanelItemSpaced>
            </StackedBody>
        </Drawer>
    );
};

export const AddAIStagingRulePanel: FC<{
    productId: number;
    productRelayId: string;
    existingRuleIds: string[];
    onBack: () => void;
    onAssigned: () => void;
    onCreateNew: () => void;
}> = (props) => {
    return (
        <Suspense
            fallback={
                <Drawer
                    isOpen={true}
                    isStacked
                    onClose={props.onBack}
                    header={{ title: T._('Add AI lifestyle scene rule', 'Admin'), subtitle: '' }}
                    width={580}
                    footer={
                        <StackedFooterButtons>
                            <FooterButton onClick={props.onBack}>{T._('Cancel', 'Admin')}</FooterButton>
                        </StackedFooterButtons>
                    }
                >
                    <StackedBody>
                        <ContentLoader
                            speed={2}
                            width='100%'
                            height='60'
                            backgroundColor={theme.skeleton.backgroundColor}
                            foregroundColor={theme.skeleton.foregroundColor}
                        >
                            <rect x='0' y='0' rx='8' ry='8' width='100%' height='16' />
                            <rect x='0' y='24' rx='8' ry='8' width='80%' height='16' />
                            <rect x='0' y='48' rx='8' ry='8' width='60%' height='16' />
                        </ContentLoader>
                    </StackedBody>
                </Drawer>
            }
        >
            <AddAIStagingRulePanelContent {...props} />
        </Suspense>
    );
};
